<script>
import { BaseItemGroup } from "@/components/ui/app/AppItemGroup";
import Touch from "@/core/directives/touch";

export default BaseItemGroup.extend({
  name: "TabsItems",

  provide() {
    return {
      tabsGroup: this
    };
  },

  directives: { Touch },

  props: {
    activeClass: {
      type: String,
      default: "tab-item_active"
    },
    mandatory: {
      type: Boolean,
      default: false
    },
    reverse: {
      type: Boolean,
      default: undefined
    },
    touch: Object,
    touchless: Boolean,
    value: {
      required: false
    },
    vertical: Boolean
  },

  data() {
    return {
      changedByDelimiters: false,
      internalHeight: undefined, // This can be fixed by child class.
      transitionHeight: undefined, // Intermediate height during transition.
      transitionCount: 0, // Number of windows in transition state.
      isBooted: false,
      isReverse: false
    };
  },

  computed: {
    isActive() {
      return this.transitionCount > 0;
    },
    classes() {
      return {
        ...BaseItemGroup.options.computed.classes.call(this)
      };
    },
    computedTransition() {
      if (!this.isBooted) return "";

      const axis = this.vertical ? "y" : "x";
      const direction = this.internalReverse ? "-reverse" : "";

      return `window-${axis}${direction}-transition`;
    },
    hasActiveItems() {
      return Boolean(this.items.find(item => !item.disabled));
    },
    hasNext() {
      return this.internalIndex < this.items.length - 1;
    },
    hasPrev() {
      return this.internalIndex > 0;
    },
    internalIndex() {
      return this.items.findIndex((item, i) => {
        return this.internalValue === this.getValue(item, i);
      });
    },
    internalReverse() {
      if (this.reverse !== undefined) return this.reverse;

      return this.isReverse;
    }
  },

  watch: {
    internalIndex: "updateReverse"
  },

  mounted() {
    window.requestAnimationFrame(() => (this.isBooted = true));
  },

  methods: {
    genContainer() {
      const children = [this.$slots.default];

      return this.$createElement(
        "div",
        {
          staticClass: "tabs-items__container",
          class: {
            "tabs-items__container_active": this.isActive
          },
          style: {
            height: this.internalHeight || this.transitionHeight
          }
        },
        children
      );
    },

    getNextIndex(index) {
      const nextIndex = (index + 1) % this.items.length;
      const item = this.items[nextIndex];

      if (item.disabled) return this.getNextIndex(nextIndex);

      return nextIndex;
    },
    getPrevIndex(index) {
      const prevIndex = (index + this.items.length - 1) % this.items.length;
      const item = this.items[prevIndex];

      if (item.disabled) return this.getPrevIndex(prevIndex);

      return prevIndex;
    },

    next() {
      this.isReverse = true;

      /* istanbul ignore if */
      if (!this.hasActiveItems || !this.hasNext) return;

      const nextIndex = this.getNextIndex(this.internalIndex);
      const item = this.items[nextIndex];

      this.internalValue = this.getValue(item, nextIndex);
    },

    prev() {
      this.isReverse = false;

      /* istanbul ignore if */
      if (!this.hasActiveItems || !this.hasPrev) return;

      const lastIndex = this.getPrevIndex(this.internalIndex);
      const item = this.items[lastIndex];

      this.internalValue = this.getValue(item, lastIndex);
    },

    updateReverse(val, oldVal) {
      if (this.changedByDelimiters) {
        this.changedByDelimiters = false;
        return;
      }

      this.isReverse = val < oldVal;
    },

    getValue(item, i) {
      return (
        item.id || BaseItemGroup.options.methods.getValue.call(this, item, i)
      );
    }
  },

  render(h) {
    const data = {
      staticClass: "tabs-items",
      class: this.classes,
      directives: []
    };

    if (!this.touchless) {
      const value = this.touch || {
        left: () => {
          this.next();
        },
        right: () => {
          this.prev();
        },
        end: e => {
          e.stopPropagation();
        },
        start: e => {
          e.stopPropagation();
        }
      };

      data.directives.push({
        name: "touch",
        value
      });
    }

    return h("div", data, [this.genContainer()]);
  }
});
</script>

<style lang="scss">
.tabs-items {
  &__container {
    height: inherit;
    position: relative;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

    &_active {
      overflow: hidden;
    }
  }

  &__prev,
  &__next {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    position: absolute;
    margin: 0 16px;
    top: calc(50% - 20px);
    z-index: 1;

    .btn:hover {
      background: none;
    }
  }

  &__prev {
    left: 0;
  }
  &__next {
    right: 0;
  }
}
</style>
